// Leitura das duas planilhas e cruzamento que produz o frete.
//
// Fica separado da tela de propósito: é tudo função pura sobre a saída do
// XLSX, sem React e sem DOM, então dá pra testar o cálculo com um arquivo de
// exemplo sem abrir o navegador.
//
// São duas planilhas de origens diferentes:
//   TABELA     — faixas de CEP (qual tarifa atende cada CEP) + preço por peso
//   FECHAMENTO — as entregas da quinzena que a transportadora está cobrando
//
// O cruzamento é: CEP do pedido → faixa → tarifa; peso do pedido → linha de
// peso; a célula onde os dois se encontram é o valor do frete.

const norm = (s) =>
  String(s == null ? '' : s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()

const clean = (s) => String(s == null ? '' : s).replace(/\s+/g, ' ').trim()

/** "R$ 1.234,56" | 1234.56 | "12%" → número. NaN quando não dá pra ler. */
export function num(v) {
  if (v == null || v === '') return NaN
  if (typeof v === 'number') return v
  let s = String(v).trim().replace(/[R$\s%]/g, '')
  // Vírgula presente = formato pt-BR: o ponto é separador de milhar.
  if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.')
  const n = Number(s)
  return Number.isFinite(n) ? n : NaN
}

/** CEP em qualquer formato → número de até 8 dígitos. null quando inválido. */
export function cepNum(v) {
  const d = String(v == null ? '' : v).replace(/\D/g, '')
  if (!d || d.length > 8) return null
  return Number(d)
}

export function cepFmt(n) {
  if (n == null) return ''
  const s = String(n).padStart(8, '0')
  return `${s.slice(0, 5)}-${s.slice(5)}`
}

/** Índice de coluna → letra do Excel (0 → A). Usado nas mensagens de aviso. */
function colLetter(i) {
  let s = ''
  let n = i + 1
  while (n > 0) {
    const m = (n - 1) % 26
    s = String.fromCharCode(65 + m) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

const pad = (n) => String(n).padStart(2, '0')

/**
 * Data da planilha → "YYYY-MM-DD HH:mm".
 *
 * O XLSX devolve três formatos conforme a célula: Date, serial do Excel
 * (dias desde 1900) ou texto "dd/mm/aaaa hh:mm". Os três passam por aqui.
 * O serial é convertido em UTC e lido em UTC de propósito — converter pro
 * fuso local jogaria a data um dia pra trás em horários perto da meia-noite.
 */
export function dateStr(v) {
  if (v == null || v === '') return ''
  let d
  if (v instanceof Date) {
    d = new Date(Date.UTC(v.getFullYear(), v.getMonth(), v.getDate(), v.getHours(), v.getMinutes()))
  } else if (typeof v === 'number') {
    d = new Date(Math.round((v - 25569) * 86400000))
  } else {
    const m = String(v).match(/(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?/)
    if (!m) return ''
    return `${m[3]}-${m[2]}-${m[1]} ${m[4] || '00'}:${m[5] || '00'}`
  }
  if (isNaN(d.getTime())) return ''
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

const rowsOf = (XLSX, ws) =>
  XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: null, blankrows: false })

/**
 * Acha a coluna pelo nome do cabeçalho. Tenta primeiro igualdade exata e só
 * depois "contém" — senão "CEP" casaria com "CEP INICIAL" e pegaria a errada.
 */
function findCol(header, names) {
  const h = header.map(norm)
  for (const n of names) {
    const i = h.indexOf(norm(n))
    if (i >= 0) return i
  }
  for (const n of names) {
    const i = h.findIndex((x) => x && x.includes(norm(n)))
    if (i >= 0) return i
  }
  return -1
}

// ── TABELA DE FRETE ────────────────────────────────────────────────────────
export function parseTabela(XLSX, wb, fileName) {
  const avisos = []
  let faixas = null
  let precos = null

  // As duas abas não têm nome fixo: são localizadas pelo cabeçalho.
  for (const name of wb.SheetNames) {
    const rows = rowsOf(XLSX, wb.Sheets[name])
    if (!faixas) {
      const hi = rows.findIndex((r) => r && r.some((cell) => norm(cell) === 'cep inicial'))
      if (hi >= 0) faixas = { sheet: name, rows, hi }
    }
    if (!precos) {
      const hi = rows.findIndex((r) => r && r.some((cell) => norm(cell).startsWith('peso min')))
      if (hi >= 0) precos = { sheet: name, rows, hi }
    }
  }
  if (!faixas) throw new Error('Não encontrei a aba de faixas de CEP (cabeçalho "CEP INICIAL").')
  if (!precos) throw new Error('Não encontrei a aba de preços por peso (cabeçalho "Peso Min").')

  // Faixas de CEP
  const h = faixas.rows[faixas.hi]
  const col = {
    ini: findCol(h, ['cep inicial']),
    fim: findCol(h, ['cep final']),
    prazo: findCol(h, ['prazo']),
    tarifa: findCol(h, ['tipo tarifa', 'tarifa']),
    uf: findCol(h, ['uf']),
    adv: findCol(h, ['advalorem (%)', 'advalorem', 'ad valorem']),
    gris: findCol(h, ['gris (%)', 'gris']),
  }
  const listaFaixas = []
  for (const r of faixas.rows.slice(faixas.hi + 1)) {
    const ini = cepNum(r[col.ini])
    const fim = cepNum(r[col.fim])
    if (ini == null || fim == null) continue
    listaFaixas.push({
      ini,
      fim,
      prazo: col.prazo >= 0 ? num(r[col.prazo]) || null : null,
      tarifa: clean(r[col.tarifa]),
      uf: col.uf >= 0 ? clean(r[col.uf]) : '',
      adv: col.adv >= 0 ? num(r[col.adv]) || 0 : 0,
      gris: col.gris >= 0 ? num(r[col.gris]) || 0 : 0,
    })
  }
  // Ordenado: a busca por CEP depende disso pra poder parar cedo.
  listaFaixas.sort((a, b) => a.ini - b.ini)
  const tarifasFaixas = [...new Set(listaFaixas.map((f) => f.tarifa))]

  // Preços por peso
  const ph = precos.rows[precos.hi]
  const cMin = ph.findIndex((x) => norm(x).startsWith('peso min'))
  const cMax = ph.findIndex((x) => norm(x).startsWith('peso max'))
  const tarCols = []
  ph.forEach((x, i) => {
    if (i !== cMin && i !== cMax && clean(x)) tarCols.push({ i, nome: clean(x) })
  })

  // A planilha chega com cabeçalhos de tarifa repetidos. Quando isso acontece
  // e sobra uma tarifa das faixas sem coluna de preço, a coluna repetida é
  // atribuída a ela — com aviso, porque é um palpite sobre a planilha.
  const usados = new Set()
  const faltando = tarifasFaixas.filter((t) => !tarCols.some((tc) => norm(tc.nome) === norm(t)))
  tarCols.forEach((tc) => {
    if (usados.has(norm(tc.nome)) && faltando.length) {
      const novo = faltando.shift()
      avisos.push(
        `Na aba "${precos.sheet}", a coluna ${colLetter(tc.i)} repete o cabeçalho "${tc.nome}". ` +
        `Ela foi lida como "${novo}", a tarifa das faixas de CEP que não tinha coluna de preço.`
      )
      tc.nome = novo
    }
    usados.add(norm(tc.nome))
  })

  const tarifas = tarCols.map((t) => t.nome)
  for (const t of tarifasFaixas) {
    if (!tarifas.some((x) => norm(x) === norm(t))) {
      avisos.push(`A tarifa "${t}" aparece nas faixas de CEP, mas não tem coluna de preço.`)
    }
  }

  const pesos = []
  let semValor = 0
  for (const r of precos.rows.slice(precos.hi + 1)) {
    const max = num(r[cMax])
    if (!Number.isFinite(max)) continue
    const valores = {}
    let algum = false
    tarCols.forEach((tc) => {
      const v = num(r[tc.i])
      if (Number.isFinite(v)) {
        valores[tc.nome] = Math.round(v * 100) / 100
        algum = true
      }
    })
    if (!algum) {
      semValor++
      continue
    }
    pesos.push({
      min: Math.round((num(r[cMin]) || 0) * 1000) / 1000,
      max: Math.round(max * 1000) / 1000,
      valores,
    })
  }
  pesos.sort((a, b) => a.max - b.max)

  if (semValor) {
    avisos.push(
      `${semValor} linhas de peso vieram sem valor. Se a planilha usa fórmulas, abra no Excel, salve e envie de novo.`
    )
  }
  if (!pesos.length) throw new Error('A aba de preços não tem linhas de peso com valor.')
  if (listaFaixas.some((f) => f.adv || f.gris)) {
    avisos.push(
      'Há faixas com Advalorem ou GRIS acima de zero. O fechamento não traz o valor da nota, então esses percentuais não entram no cálculo.'
    )
  }

  return { kind: 'tabela', fileName: fileName || '', faixas: listaFaixas, tarifas, pesos, avisos }
}

// ── FECHAMENTO ─────────────────────────────────────────────────────────────
export function parseFechamento(XLSX, wb, fileName) {
  let rows = null
  let hi = -1
  let sheet = ''
  for (const name of wb.SheetNames) {
    const rs = rowsOf(XLSX, wb.Sheets[name])
    const i = rs.findIndex(
      (r) => r && r.some((c) => norm(c) === 'cep') && r.some((c) => norm(c).startsWith('peso'))
    )
    if (i >= 0) {
      rows = rs
      hi = i
      sheet = name
      break
    }
  }
  if (!rows) throw new Error('Não encontrei o cabeçalho do fechamento (colunas "CEP" e "Peso (KG)").')

  const h = rows[hi]
  const col = {
    tipo: findCol(h, ['tipo / doc']),
    nf: findCol(h, ['numero', 'nota', 'nf']),
    vol: findCol(h, ['quantidade de volumes', 'volumes']),
    peso: findCol(h, ['peso (kg)', 'peso']),
    mot: findCol(h, ['motorista']),
    dest: findCol(h, ['destinatario']),
    cep: findCol(h, ['cep']),
    cidade: findCol(h, ['cidade']),
    bairro: findCol(h, ['bairro']),
    uf: findCol(h, ['uf']),
    rota: findCol(h, ['rota']),
    sit: findCol(h, ['situacao']),
    oc: findCol(h, ['motorista / entrega finalizacao - ocorrencia', 'ocorrencia']),
    status: findCol(h, ['status']),
    dtInc: findCol(h, ['inclusao - data / hora']),
    dtFin: findCol(h, ['motorista / entrega finalizacao - data / hora']),
  }
  if (col.nf < 0) throw new Error('Não encontrei a coluna "Número" (nota fiscal).')

  const g = (r, i) => (i >= 0 ? r[i] : null)
  const pedidos = []
  for (const r of rows.slice(hi + 1)) {
    const nf = clean(g(r, col.nf))
    const cep = cepNum(g(r, col.cep))
    if (!nf && cep == null) continue
    // A planilha cola telefone no motorista e CPF/CNPJ no destinatário.
    const mot = clean(g(r, col.mot)).replace(/\s*\+?55\s*\d[\d\s-]*$/, '').trim()
    const dest = clean(g(r, col.dest)).replace(/\s*-\s*\d{11,14}\s*$/, '').trim()
    const peso = num(g(r, col.peso))
    pedidos.push({
      nf,
      vol: num(g(r, col.vol)) || 0,
      peso: Number.isFinite(peso) ? Math.round(peso * 1000) / 1000 : null,
      dest,
      cep,
      cidade: clean(g(r, col.cidade)),
      bairro: clean(g(r, col.bairro)),
      uf: clean(g(r, col.uf)),
      rota: clean(g(r, col.rota)).toUpperCase(),
      mot,
      sit: clean(g(r, col.sit)),
      oc: clean(g(r, col.oc)).toUpperCase(),
      status: clean(g(r, col.status)),
      dtInc: dateStr(g(r, col.dtInc)),
      dtFin: dateStr(g(r, col.dtFin)),
    })
  }
  if (!pedidos.length) throw new Error('O fechamento não tem pedidos abaixo do cabeçalho.')
  return { kind: 'fechamento', fileName: fileName || '', sheet, pedidos, avisos: [] }
}

export const fmtKg = (n) => Number(n).toLocaleString('pt-BR', { maximumFractionDigits: 2 })

// ── CRUZAMENTO ─────────────────────────────────────────────────────────────
/**
 * Para cada pedido do fechamento: acha a faixa de CEP, a faixa de peso e o
 * valor na interseção das duas. `flags` marca o que precisa de conferência
 * antes de fechar a fatura.
 */
export function calcular(tabela, fech) {
  if (!tabela || !fech) return null
  const { faixas, pesos } = tabela
  const pesoMax = pesos[pesos.length - 1].max

  const nfCount = {}
  fech.pedidos.forEach((p) => {
    nfCount[p.nf] = (nfCount[p.nf] || 0) + 1
  })

  const linhas = fech.pedidos.map((p, idx) => {
    const flags = []

    let faixa = null
    if (p.cep != null) {
      for (const f of faixas) {
        if (p.cep >= f.ini && p.cep <= f.fim) {
          faixa = f
          break
        }
        // A lista está ordenada por CEP inicial: passou do CEP, não acha mais.
        if (f.ini > p.cep) break
      }
    }
    if (!faixa) flags.push('cep')

    const peso = p.peso == null ? 0 : p.peso
    if (p.peso == null || p.peso === 0) flags.push('peso0')

    let faixaPeso = null
    if (peso > pesoMax) {
      flags.push('pesoMax')
    } else {
      // Tolerância de 1e-9: peso 10.000 vs faixa "até 10" falha em float puro.
      for (const fp of pesos) {
        if (peso <= fp.max + 1e-9) {
          faixaPeso = fp
          break
        }
      }
    }

    let frete = null
    if (faixa && faixaPeso) {
      const k = Object.keys(faixaPeso.valores).find((t) => norm(t) === norm(faixa.tarifa))
      if (k != null) frete = faixaPeso.valores[k]
      else flags.push('semPreco')
    }

    const entregue = /ENTREGUE/.test(p.oc) && !/^NAO ENTREGUE/.test(norm(p.status).toUpperCase())
    if (!entregue) flags.push('naoEntregue')
    if (nfCount[p.nf] > 1) flags.push('dup')

    return {
      ...p,
      idx,
      tarifa: faixa ? faixa.tarifa : '',
      prazo: faixa ? faixa.prazo : null,
      faixaCep: faixa ? `${cepFmt(faixa.ini)} a ${cepFmt(faixa.fim)}` : '',
      faixaPeso: faixaPeso
        ? faixaPeso.min
          ? `${fmtKg(faixaPeso.min)} a ${fmtKg(faixaPeso.max)} kg`
          : `até ${fmtKg(faixaPeso.max)} kg`
        : '',
      frete,
      flags,
    }
  })

  return { linhas, pesoMax }
}

export { norm }
